"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useBridge } from "../lib/bridge";
import { MessageType } from "@/src/lib/bridge/types";

const NOTIFICATION_PERMISSION_QUERY_KEY = ["notificationPermission"] as const;

/**
 * 统一查询 App 内通知权限状态，与 Bridge NOTIFICATION_PERMISSION 同步。
 * 多处复用同一 queryKey，便于共享缓存。
 *
 * 缓存策略：结果长期缓存，仅在调用方执行 invalidateQueries(NOTIFICATION_PERMISSION_QUERY_KEY) 时
 * 失效并重新请求（例如 NotificationSection 在 APP_FOREGROUND 时调用）。
 */
export function useNotificationPermission(extraEnabled = true) {
  const { request, isInApp, ready } = useBridge();
  const queryClient = useQueryClient();
  const query = useQuery({
    // In pure Web, Bridge.request() never reaches native (sendToApp no-op) and hits the 30s timeout.
    enabled: !!request && extraEnabled && isInApp && ready,
    queryKey: NOTIFICATION_PERMISSION_QUERY_KEY,
    queryFn: async () => {
      const res = await request(MessageType.NOTIFICATION_PERMISSION, {
        action: "check",
      });
      return res.data;
    },
    // staleTime: Infinity,
    // gcTime: 1000 * 60 * 60,
  });
  const requestNotificationPermission = async () => {
    const res = await request(MessageType.REQUEST_NOTIFICATION_PERMISSION, {
      action: "request",
    });
    const data = (res?.data ?? {}) as {
      dialogShown?: boolean;
      enabled?: boolean;
      status?: string;
    };
    const dialogShown = Boolean(data.dialogShown); // 是否显示过弹窗
    // Android 授权瞬间 areNotificationsEnabled 可能滞后；status=granted 作兜底
    const enabled = data.enabled === true || data.status === "granted";
    if (enabled) {
      queryClient.invalidateQueries({
        queryKey: NOTIFICATION_PERMISSION_QUERY_KEY,
      });
      return { enabled: true };
    }
    if (!dialogShown) {
      request(MessageType.NOTIFICATION_PERMISSION, {
        action: "open-settings",
      });
      return { enabled: false, action: "open-settings" };
    }
    return { enabled: false };
  };
  return { ...query, requestNotificationPermission };
}

export { NOTIFICATION_PERMISSION_QUERY_KEY };
